<template>
  <div class="position-relative">
    <div
      v-if="disabled"
      style="position: absolute; inset: 0; z-index: 9999; cursor: not-allowed"
    ></div>
    <div v-if="label != null" class="text-subtitle-1">{{ label }}</div>
    <file-pond
      style="background-color: #ffffff"
      :key="key + files"
      :ref="key"
      name="file"
      :id="key"
      label-idle="<span class='filepond--label-action badge badge-primary ml-2'>Pilih Berkas</span>"
      v-bind:files="files"
      v-on:init="handleFilePondInit"
      :allow-browse="!disabled"
      :allow-drop="!disabled"
      :allow-paste="!disabled"
      :allow-multiple="multiple"
      :allow-remove="allow_remove"
      :max-files="maxFiles"
      :max-file-size="max_file_size"
      :server="server"
      :onprocessfile="uploaded"
      :onprocessfiles="onFinish"
      :onactivatefile="fileClicked"
      :onremovefile="removeFile"
      :v-bind:required="required"
      :accepted-file-types="acceptedFileTypes"
      :allow-image-crop="allowImageCrop"
      :allow-image-transform="needsImageTransform"
      :image-resize-target-width="imageResizeTargetWidth"
      :image-resize-target-height="imageResizeTargetHeight"
      :image-crop-aspect-ratio="imageCropAspectRatio"
      :credits="false"
    />
    
    <!-- Camera Capture Button for profile pictures -->
    <div v-if="showCameraButton && !hasFiles" class="mt-2">
      <v-btn
        color="primary"
        outlined
        class="w-100"
        @click="openCamera"
        :disabled="disabled"
      >
        <v-icon left small>mdi-camera</v-icon>
        Ambil Foto
      </v-btn>
    </div>
    
    <!-- Camera Capture Modal -->
    <camera-capture
      ref="cameraCapture"
      @photo-captured="handlePhotoCaptured"
    />
  </div>
</template>

<script>
import vueFilePond, { setOptions } from "vue-filepond";

// Import plugins
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type/dist/filepond-plugin-file-validate-type.esm.js";
import FilePondPluginImagePreview from "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.esm.js";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginMediaPreview from "filepond-plugin-media-preview";
import FilePondPluginImageCrop from "filepond-plugin-image-crop";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";

// Import styles
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import "filepond-plugin-media-preview/dist/filepond-plugin-media-preview.min.css";

import JwtService from "@/core/services/jwt.service";
import ApiService from "@/core/services/api.service";
import CameraCapture from "./CameraCapture.vue";

import { isArray, forEach, find, floor } from "lodash";

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginMediaPreview,
  FilePondPluginImageCrop,
  FilePondPluginImageResize,
  FilePondPluginImageTransform,
);

export default {
  props: {
    value: [Array, String],
    category: {
      type: String,
      default: null
    },
    acceptedFileTypes: {
      type: String,
      default: null
    },
    max_file_size: {
      type: String,
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: Number,
      default: 5
    },
    onSuccess: {
      type: Function,
      default: function() {}
    },
    onSuccessDelete: {
      type: Function,
      default: function() {}
    },
    allow_remove: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    allowImageCrop: {
      type: Boolean,
      default: false
    },
    imageResizeTargetWidth: {
      type: Number,
      default: null
    },
    imageResizeTargetHeight: {
      type: Number,
      default: null
    },
    imageCropAspectRatio: {
      type: String,
      default: null
    },
    key: [Array, String, Object],
    showCameraButton: {
      type: Boolean,
      default: false
    }
  },
  async mounted() {
    await this.fetchCategory();
    await this.fetchInput();
  },
  data() {
    return {
      serverId: null,
      ids: [],
      filesData: [],
      files: [],
      fileUrl: null,
      inside: false,
      server: {
        url: process.env.VUE_APP_SERVER_URL,
        instantUpload: false,
        process: {
          url: "/file/upload",
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${JwtService.getToken()}`
          }
        },
        load: async (uniqueFileId, load, error, progress, abort) => {
          const objfile = find(this.filesData, { id: uniqueFileId });
          if (objfile) {
            await fetch(objfile.url)
              .then(async res => await res.blob())
              .then(async fl => {
                fl.name = objfile.name;
                await load(fl);
              })
              .catch(() => {
                abort();
              });
          }
        },
        revert: async (source, load, error) => {
          try {
            await ApiService.delete(
              "file/delete?id=" + source + "&action=revert"
            );
            this.removeFile(source);
          } catch (err) {
            error(err);
          }
        }
      }
    };
  },
  methods: {
    getFileID(serverId) {
      try {
        return serverId ? JSON?.parse(serverId)?.id : null;
      } catch (error) {
        return serverId ? serverId : null;
      }
    },
    handleFilePondInit: function() {
      // console.log("FilePond has initialized");
      // example of instance method call on pond reference
      //   this.$refs.pond.getFiles();
    },
    fileClicked(file) {
      const objfile = find(this.filesData, {
        id: this.getFileID(file.serverId)
      });
      window.open(objfile.url);
    },
    async uploaded(err, file) {
      this.ids.unshift(this.getFileID(file.serverId));
      this.bindInput();
    },
    async onFinish() {
      this.onSuccess();
    },
    async refresh() {
      if (this.ids.length <= 0) {
        this.files = [];
        this.filesData = [];
        return;
      }
      const { data } = await ApiService.post("file/load", {
        fileId: this.ids
      });

      let _filesData = [];
      let _files = [];
      await forEach(data, value => {
        _filesData.push(value); // id, name, size, type, url
        _files.push({
          source: value.id,
          options: {
            type: "local"
          }
        });
      });

      this.files = _files;
      this.filesData = _filesData;
    },
    async removeFile(err, file) {
      if (!file) {
        return;
      }

      const id = this.getFileID(file.serverId);
      if (id) {
        await ApiService.delete("file/delete?id=" + id);
        this.ids.splice(this.ids.indexOf(id), 1);
        this.bindInput();
        await this.refresh(); // Refresh to update files array
        this.onSuccessDelete();
      }
    },
    bindInput() {
      const ids = this.ids;
      this.inside = true;
      if (!this.multiple) {
        if (ids.length > 0) {
          this.$emit("input", ids[0]);
        } else {
          this.$emit("input", null);
        }
      } else {
        this.$emit("input", ids);
      }
    },
    async fetchInput() {
      if (this.value) {
        let ids = [];
        if (!isArray(this.value)) {
          ids = [this.value];
        } else {
          ids = this.value;
        }

        this.ids = ids;
        await this.refresh();
      } else {
        this.ids = [];
        await this.refresh();
      }

      if (this.key) {
        this.$refs[this.key].server = this.server;
      } else {
        setOptions({
          server: this.server,
          credits: false,
        });
      }
    },
    async fetchCategory() {
      if (this.category) {
        // fetch category obj
        const { data } = await ApiService.get(
          "file/category?slug=" + this.category
        );
        this.acceptedFileTypes = data.allowed_extension.join(",");

        this.server.process.url = "/file/upload/" + data.slug;
        if (data.max_file_size) {
          this.max_file_size = data.max_file_size + "KB";
        } else {
          this.max_file_size = null;
        }
      } else {
        const { data } = await ApiService.get("file/default");
        this.server.process.url = "/file/upload";
        this.max_file_size = floor(data.max_file_size / 1024 / 1024) + "MB";
        this.acceptedFileTypes = data.acceptedFileTypes;
      }

      if (this.key) {
        this.$refs[this.key].server = this.server;
      } else {
        // setOptions({
        //   server: this.server
        // });
      }
    },
    
    openCamera() {
      if (this.$refs.cameraCapture) {
        this.$refs.cameraCapture.open();
      }
    },
    
    async handlePhotoCaptured(file) {
      try {
        // Create FormData to upload the captured photo
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload the file using the same endpoint as FilePond
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}${this.server.process.url}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${JwtService.getToken()}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.text(); // FilePond expects text response
          // Simulate FilePond file upload completion
          this.ids.unshift(this.getFileID(result));
          this.bindInput();
          await this.refresh();
          this.onSuccess();
        } else {
          throw new Error('Upload failed');
        }
      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'Gagal mengunggah foto',
          text: error?.response?.data?.message || error?.message || 'Terjadi kesalahan saat mengunggah foto.',
          confirmButtonText: 'Tutup'
        });
      }
    }
  },
  components: {
    FilePond,
    CameraCapture
  },
  computed: {
    hasFiles() {
      // Primary check on ids array as it's the authoritative source
      return this.ids.length > 0;
    },
    needsImageTransform() {
      // Tanpa ini, filepond-plugin-image-transform re-encode SEMUA gambar
      // lewat canvas sebelum upload walau tidak ada crop/resize, sehingga
      // ukuran file bisa membengkak dari aslinya dan gagal cek max_file_size.
      return this.allowImageCrop || !!this.imageResizeTargetWidth || !!this.imageResizeTargetHeight;
    }
  },
  watch: {
    category() {
      this.fetchCategory();
    },
    value() {
      if (!this.inside) {
        this.fetchInput();
      }
      this.inside = false;
    }
  }
};
</script>

<style lang="scss">
.filepond--root .filepond--drop-label {
  background-color: transparent !important;
}
.filepond--label-action {
  text-decoration: none;
  cursor: pointer;
}
.filepond--root {
  background-color: transparent !important;
  margin-bottom: 10px;
}
.filepond--panel {
  background-color: transparent !important;
}
.filepond--panel-root {
  background-color: transparent !important;
  border: 1px solid #ccc;
  border-radius: 10px;
}
.filepond--panel-bottom.filepond--panel-root {
  padding-bottom: 10px;
}
</style>
